import { useState } from "react";
import { Tabs } from "../ui/Tabs.js";
import {
  spreadsheetColumnLabel,
  type WorkbookPreview,
} from "../../lib/xlsxPreview.js";
import { workbookRowGroups } from "./workbookPreviewModel.js";

export const PohodaWorkbookPreviewTable = ({
  preview,
}: {
  preview: WorkbookPreview;
}) => {
  const [selection, setSelection] = useState({
    preview,
    name: preview.sheets[0]?.name ?? "",
  });
  if (selection.preview !== preview) {
    setSelection({ preview, name: preview.sheets[0]?.name ?? "" });
  }
  const selectedSheet =
    selection.preview === preview ? selection.name : preview.sheets[0]?.name;
  const sheet =
    preview.sheets.find((item) => item.name === selectedSheet) ??
    preview.sheets[0];
  if (!sheet)
    return (
      <p className="p-5 text-sm text-foreground-muted">
        This workbook has no visible sheets.
      </p>
    );

  const columnIndexes = Array.from(
    { length: sheet.columnCount },
    (_value, index) => index,
  );
  const { vacationWarnings, groupSpans, groupNumbers } =
    workbookRowGroups(sheet);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_18px_45px_rgba(0,0,0,0.16)]">
      <Tabs
        selectedKey={sheet.name}
        onSelectionChange={(key: string | number) =>
          setSelection({ preview, name: String(key) })
        }
      >
        <Tabs.List
          aria-label="Pohoda workbook sheets"
          className="flex items-center gap-1 overflow-x-auto border-b border-border bg-elevated px-2 pt-2"
        >
          {preview.sheets.map((item) => (
            <Tabs.Tab
              key={item.name}
              id={item.name}
              className={`relative w-auto! shrink-0 rounded-t-lg! border border-b-0 px-3 py-2 text-sm font-medium ${item.name === sheet.name ? "border-border bg-background text-foreground" : "border-transparent text-foreground-muted"}`}
            >
              {item.name}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Tabs.Panel
          id={sheet.name}
          aria-label={`${sheet.name} worksheet`}
          className="max-h-[min(62vh,42rem)] overflow-auto"
        >
          <table className="min-w-max w-full border-separate border-spacing-0 text-left text-sm tabular-nums">
            <caption className="sr-only">
              {sheet.name} worksheet preview
            </caption>
            <thead className="sticky top-0 z-20 bg-elevated text-foreground-muted shadow-[0_1px_0_var(--color-border)]">
              <tr>
                <th
                  scope="col"
                  aria-label="Row number"
                  className="sticky left-0 top-0 z-30 w-12 min-w-12 border-b border-r border-border bg-elevated px-3 py-2"
                />
                {columnIndexes.map((columnIndex) => (
                  <th
                    key={columnIndex}
                    scope="col"
                    className="min-w-32 border-b border-r border-border bg-elevated px-3 py-2 text-center font-mono text-[11px] font-semibold tracking-[0.12em] text-foreground-subtle last:border-r-0"
                  >
                    {spreadsheetColumnLabel(columnIndex)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sheet.rows.map((row, rowIndex) => {
                const vacationWarning = vacationWarnings.has(rowIndex);
                const groupNumber = groupNumbers.get(rowIndex);
                const groupStart = groupSpans.has(rowIndex);
                const groupStripe =
                  groupNumber !== undefined && groupNumber % 2 === 1
                    ? "bg-surface/55"
                    : "";
                const groupBorder = groupStart
                  ? "border-t-2 border-t-accent/55"
                  : "";
                return (
                  <tr
                    key={rowIndex}
                    title={
                      vacationWarning
                        ? "Vacation exceeds 8 hours for this day"
                        : undefined
                    }
                    className={`group ${vacationWarning ? "bg-danger-subtle" : "hover:bg-accent-subtle/35"}`}
                  >
                    <th
                      scope="row"
                      className={`sticky left-0 z-10 border-b border-r border-border bg-elevated px-3 py-2 text-center font-mono text-[11px] font-medium text-foreground-subtle group-hover:bg-accent-subtle ${groupBorder}`}
                    >
                      {rowIndex + 1}
                    </th>
                    {columnIndexes.map((cellIndex) => {
                      const groupedEmployeeCell =
                        rowIndex > 0 && cellIndex === sheet.groupByColumn;
                      if (groupedEmployeeCell && !groupStart) return null;
                      if (groupedEmployeeCell) {
                        const span = groupSpans.get(rowIndex) ?? 1;
                        return (
                          <th
                            key={cellIndex}
                            scope="rowgroup"
                            rowSpan={span}
                            title={row[cellIndex] || undefined}
                            className={`min-w-60 max-w-72 border-b border-r border-border px-4 py-3 text-left align-top text-foreground ${groupBorder} ${groupStripe || "bg-background"}`}
                          >
                            <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
                              Employee
                            </span>
                            <span className="mt-1 block whitespace-normal font-semibold leading-5">
                              {row[cellIndex]}
                            </span>
                            <span className="mt-1 block text-[11px] font-normal text-foreground-muted">
                              {span} {span === 1 ? "absence" : "absences"}
                            </span>
                          </th>
                        );
                      }
                      return (
                        <td
                          key={cellIndex}
                          title={row[cellIndex] || undefined}
                          className={`max-w-[28rem] whitespace-nowrap border-b border-r border-border px-3 py-2 text-foreground last:border-r-0 ${rowIndex === 0 ? "bg-accent-subtle/45 font-semibold group-hover:bg-accent-subtle/70" : vacationWarning ? "bg-danger-subtle" : groupStripe} ${groupBorder}`}
                        >
                          {row[cellIndex]}
                          {vacationWarning && cellIndex === 4 ? (
                            <span className="sr-only">
                              {" "}
                              — Vacation exceeds 8 hours for this day
                            </span>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {sheet.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={Math.max(sheet.columnCount + 1, 1)}
                    className="px-4 py-14 text-center text-sm text-foreground-muted"
                  >
                    This sheet is empty.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};
